/**
 * Compute ASTRA cone_vec geometry for inclined laminography (and tomography,
 * which is the same math with tiltX = 90deg).
 *
 * @param {Object} p - Geometry parameters
 * @param {number} p.numOfImgs - Number of projection angles
 * @param {number[]} p.angles - Array of projection angles in degrees
 * @param {number} p.tiltX - Tilt angle around X axis in degrees (theta)
 * @param {number} p.tiltY - Tilt angle around Y axis in degrees (beta)
 * @param {number} p.sod - Source-to-object distance
 * @param {number} p.sdd - Source-to-detector distance
 * @param {number} p.detPitch - Detector pixel pitch
 * @param {number} [p.offsetU=0] - Detector U offset
 * @param {number} [p.offsetV=0] - Detector V offset
 * @returns {number[][]} Array of rows [Sx Sy Sz Dx Dy Dz Ux Uy Uz Vx Vy Vz]
 */
export function computeInclinedGeom(p) {
  const deg2rad = (d) => (d * Math.PI) / 180;

  const tiltXRad = deg2rad(p.tiltX);
  const tiltYRad = deg2rad(p.tiltY);

  const effSod = p.sod;
  const effOdd = p.sdd - p.sod;
  const effPixel = p.detPitch;
  const offsetU = p.offsetU || 0;
  const offsetV = p.offsetV || 0;

  const sinTx = Math.sin(tiltXRad);
  const cosTx = Math.cos(tiltXRad);
  const sinTy = Math.sin(tiltYRad);
  const cosTy = Math.cos(tiltYRad);

  const vecs = [];

  for (let i = 0; i < p.numOfImgs; i++) {
    const phi = -deg2rad(p.angles[i]);
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // R = Rx(-theta) . Ry(beta) . Rz(-phi)
    const r00 = cosTy * cosPhi;
    const r01 = cosTy * sinPhi;
    const r02 = -sinTy;

    const r10 = sinTx * sinTy * cosPhi - cosTx * sinPhi;
    const r11 = sinTx * sinTy * sinPhi + cosTx * cosPhi;
    const r12 = sinTx * cosTy;

    const r20 = cosTx * sinTy * cosPhi + sinTx * sinPhi;
    const r21 = cosTx * sinTy * sinPhi - sinTx * cosPhi;
    const r22 = cosTx * cosTy;

    const row = new Array(12).fill(0);

    // S = -row2 * eff_sod
    row[0] = -effSod * r20;
    row[1] = -effSod * r21;
    row[2] = -effSod * r22;

    // D = +row2 * eff_odd
    row[3] = effOdd * r20;
    row[4] = effOdd * r21;
    row[5] = effOdd * r22;

    // U = +row0 * eff_pixel
    row[6] = effPixel * r00;
    row[7] = effPixel * r01;
    row[8] = effPixel * r02;

    // V = -row1 * eff_pixel
    row[9] = -effPixel * r10;
    row[10] = -effPixel * r11;
    row[11] = -effPixel * r12;

    // Apply detector offsets along U and V axes
    row[3] += offsetU * row[6] + offsetV * row[9];
    row[4] += offsetU * row[7] + offsetV * row[10];
    row[5] += offsetU * row[8] + offsetV * row[11];

    vecs.push(row);
  }

  return vecs;
}

export default computeInclinedGeom;
